import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import Layout from './components/layout/Layout';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import DashboardPage from './pages/DashboardPage';
import SessionsPage from './pages/SessionsPage';
import ReplayPage from './pages/ReplayPage';
import HeatmapsPage from './pages/HeatmapsPage';
import FunnelsPage from './pages/FunnelsPage';
import CheckoutInsightsPage from './pages/CheckoutInsightsPage';
import LandingPagesPage from './pages/LandingPagesPage';
import FormsPage from './pages/FormsPage';
import ErrorsPage from './pages/ErrorsPage';
import SurveysPage from './pages/SurveysPage';
import InsightsPage from './pages/InsightsPage';
import SettingsPage from './pages/SettingsPage';
import BillingPage from './pages/BillingPage';
import SharePage from './pages/SharePage';
import PlanSelectPage from './pages/onboarding/PlanSelectPage';
import SetupPage from './pages/onboarding/SetupPage';

// Admin pages (code-split)
const AdminLayout = React.lazy(() => import('./pages/admin/AdminLayout'));
const CustomersListPage = React.lazy(() => import('./pages/admin/CustomersListPage'));
const CustomerDetailPage = React.lazy(() => import('./pages/admin/CustomerDetailPage'));
const PlansManagementPage = React.lazy(() => import('./pages/admin/PlansManagementPage'));
const RevenueDashboardPage = React.lazy(() => import('./pages/admin/RevenueDashboardPage'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function RequireSuperAdmin({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  const userJson = localStorage.getItem('vantage_user');
  const storedUser = userJson ? JSON.parse(userJson) : null;
  if (!storedUser?.isSuperAdmin && !(user as any)?.isSuperAdmin) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function AdminFallback() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-sm text-gray-500">Loading admin panel...</div>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/share/:token" element={<SharePage />} />

      {/* Onboarding routes (auth required, no sidebar layout) */}
      <Route
        path="/onboarding/plan"
        element={
          <ProtectedRoute>
            <PlanSelectPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/setup"
        element={
          <ProtectedRoute>
            <SetupPage />
          </ProtectedRoute>
        }
      />

      {/* Main app routes (auth required, with layout) */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="sessions" element={<SessionsPage />} />
        <Route path="sessions/:sessionId" element={<ReplayPage />} />
        <Route path="heatmaps" element={<HeatmapsPage />} />
        <Route path="funnels" element={<FunnelsPage />} />
        <Route path="checkout-insights" element={<CheckoutInsightsPage />} />
        <Route path="landing-pages" element={<LandingPagesPage />} />
        <Route path="forms" element={<FormsPage />} />
        <Route path="errors" element={<ErrorsPage />} />
        <Route path="surveys" element={<SurveysPage />} />
        <Route path="insights" element={<InsightsPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Admin routes (super admin only, code-split) */}
      <Route
        path="/admin"
        element={
          <RequireSuperAdmin>
            <Suspense fallback={<AdminFallback />}>
              <AdminLayout />
            </Suspense>
          </RequireSuperAdmin>
        }
      >
        <Route index element={<Navigate to="/admin/customers" replace />} />
        <Route
          path="customers"
          element={
            <Suspense fallback={<AdminFallback />}>
              <CustomersListPage />
            </Suspense>
          }
        />
        <Route
          path="customers/:orgId"
          element={
            <Suspense fallback={<AdminFallback />}>
              <CustomerDetailPage />
            </Suspense>
          }
        />
        <Route
          path="plans"
          element={
            <Suspense fallback={<AdminFallback />}>
              <PlansManagementPage />
            </Suspense>
          }
        />
        <Route
          path="revenue"
          element={
            <Suspense fallback={<AdminFallback />}>
              <RevenueDashboardPage />
            </Suspense>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
